import {
  Button,
  capitalize,
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  couplerTypeToLocaleString,
  hitchTypeToLocaleString,
  ptoSpeedToLocaleString,
} from "common/utils/enumTranslations";
import {
  ErrorMessage,
  Field,
  FieldArray,
  FieldProps,
  useFormikContext,
} from "formik";
import { FormikDimensionedNumberInput } from "common/components/FormikDimensionedNumberInput";
import { getOutputGuid } from "portal/utils/ids";
import { HitchType, HydraulicCouplerType, PtoSpeed } from "protos/atk/enums";
import { negateVal } from "common/utils/negateVal";
import { NumberInput } from "common/components/NumberInput";
import { PtoConfig, ScvOutput, TractorArchetype } from "protos/atk/autotractor";
import { StepProps } from "./StepProps";
import { TractorArchetypeViewModel } from "../TractorArchetypeViewModel";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import React, { FC } from "react";

export const ConnectionsStep: FC<StepProps> = ({ disabled }) => {
  const { values, setFieldValue, setFieldTouched } =
    useFormikContext<TractorArchetypeViewModel>();
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2">
      <Paper className="flex flex-col p-4">
        <Typography className="font-bold">
          {capitalize(t("models.equipment.hitch_other"))}
        </Typography>
        <div className="px-4 py-2">
          <Typography>{capitalize(t("utils.descriptors.front"))}</Typography>
          <div className="flex flex-col gap-2 px-4">
            <HitchInput
              type={HitchType.HITCH_TYPE_THREE_POINT_FRONT}
              disabled={disabled}
            />
          </div>
          <Typography>{capitalize(t("utils.descriptors.rear"))}</Typography>
          <div className="flex flex-col gap-2 px-4">
            <HitchInput
              type={HitchType.HITCH_TYPE_THREE_POINT_REAR}
              disabled={disabled}
            />
            <HitchInput
              type={HitchType.HITCH_TYPE_DRAW_BAR_REAR}
              disabled={disabled}
            />
          </div>
        </div>
      </Paper>
      <Paper className="flex flex-col gap-2 p-4">
        <Typography className="font-bold">
          {t("models.equipment.pto_short")}
        </Typography>
        <div className="px-2">
          <FormControlLabel
            checked={Boolean(values.frontPto)}
            disabled={disabled}
            onChange={() =>
              values.frontPto
                ? setFieldValue("frontPto", undefined)
                : setFieldValue("frontPto", PtoConfig.fromPartial({}))
            }
            control={<Checkbox />}
            label={capitalize(t("utils.descriptors.front"))}
          ></FormControlLabel>
          {values.frontPto && (
            <div className="p-4 flex flex-col gap-2 bg-opacity-10 bg-black rounded-lg">
              <Typography>
                {capitalize(t("utils.descriptors.speed_other"))}
              </Typography>
              <FieldArray
                name="frontPto.availableSpeeds"
                render={(arrayHelpers) => (
                  <FormGroup row className="justify-evenly">
                    {[
                      PtoSpeed.PTO_SPEED_540,
                      PtoSpeed.PTO_SPEED_540E,
                      PtoSpeed.PTO_SPEED_1000E,
                      PtoSpeed.PTO_SPEED_1000,
                    ].map((s) => (
                      <FormControlLabel
                        disabled={disabled}
                        key={`front-speed-${s}`}
                        onChange={() => {
                          const indexofSpeed =
                            values.frontPto?.availableSpeeds.indexOf(s) ?? -1;
                          if (indexofSpeed === -1) {
                            arrayHelpers.push(s);
                          } else {
                            arrayHelpers.remove(indexofSpeed);
                          }
                        }}
                        checked={values.frontPto?.availableSpeeds.includes(s)}
                        control={<Checkbox />}
                        label={ptoSpeedToLocaleString(s)}
                      />
                    ))}
                  </FormGroup>
                )}
              />
            </div>
          )}
        </div>
        <div className="px-2">
          <FormControlLabel
            checked={Boolean(values.rearPto)}
            disabled={disabled}
            onChange={() =>
              values.rearPto
                ? setFieldValue("rearPto", undefined)
                : setFieldValue("rearPto", PtoConfig.fromPartial({}))
            }
            control={<Checkbox />}
            label={capitalize(t("utils.descriptors.rear"))}
          ></FormControlLabel>
          {values.rearPto && (
            <div className="p-4 flex flex-col gap-2 bg-opacity-10 bg-black rounded-lg">
              <Typography>
                {capitalize(t("utils.descriptors.speed_other"))}
              </Typography>
              <FieldArray
                name="rearPto.availableSpeeds"
                render={(arrayHelpers) => (
                  <FormGroup row className="justify-evenly">
                    {[
                      PtoSpeed.PTO_SPEED_540,
                      PtoSpeed.PTO_SPEED_540E,
                      PtoSpeed.PTO_SPEED_1000E,
                      PtoSpeed.PTO_SPEED_1000,
                    ].map((s) => (
                      <FormControlLabel
                        disabled={disabled}
                        key={`front-speed-${s}`}
                        onChange={() => {
                          const indexofSpeed =
                            values.rearPto?.availableSpeeds.indexOf(s) ?? -1;
                          if (indexofSpeed === -1) {
                            arrayHelpers.push(s);
                          } else {
                            arrayHelpers.remove(indexofSpeed);
                          }
                        }}
                        checked={values.rearPto?.availableSpeeds.includes(s)}
                        control={<Checkbox />}
                        label={ptoSpeedToLocaleString(s)}
                      />
                    ))}
                  </FormGroup>
                )}
              />
            </div>
          )}
        </div>
      </Paper>
      <FieldArray
        name="scvOutputs.scvOutput"
        render={(arrayHelpers) => (
          <Paper className="flex flex-col gap-2 p-4">
            <Typography className="font-bold">
              {capitalize(t("models.equipment.scv_short", { count: 1 }))}
            </Typography>
            <div className="flex flex-col gap-2">
              {values.scvOutputs?.scvOutput.map((v: ScvOutput, i) => {
                return (
                  <div
                    key={`hitch-${i}`}
                    className="p-4 bg-opacity-10 bg-black rounded-lg flex flex-col gap-2"
                  >
                    <div className="flex justify-between">
                      <div className="flex flex-col">
                        <div className="flex gap-2">
                          <Typography>
                            {capitalize(t("models.equipment.port"))}
                          </Typography>
                          <Tooltip
                            title={t(
                              "views.autotractor.equipment.portInfoTooltip"
                            )}
                            placement="top"
                          >
                            <InfoOutlinedIcon
                              className="flex my-auto"
                              fontSize="small"
                            />
                          </Tooltip>
                        </div>
                        <NumberInput
                          textFieldProps={{ disabled }}
                          label=""
                          min={0}
                          onChange={(n) => {
                            setFieldTouched(
                              `scvOutputs.scvOutput[${i}].port`,
                              true,
                              true
                            );
                            arrayHelpers.replace(i, { ...v, port: n });
                          }}
                          error=""
                          value={v.port}
                        />
                        <ErrorMessage
                          name={`scvOutputs.scvOutput[${i}].port`}
                          render={(e) => t(e)}
                        />
                      </div>
                      <IconButton
                        disabled={disabled}
                        color="info"
                        className="my-auto"
                        onClick={() => arrayHelpers.remove(i)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </div>
                    <div className="flex flex-col gap-4">
                      <div className="flex gap-2">
                        <div className="flex flex-col">
                          <Typography>
                            {capitalize(t("models.equipment.couplerDiameter"))}
                          </Typography>
                          <Field name={`scvOutputs.scvOutput[${i}].coupler`}>
                            {({
                              field,
                              meta,
                            }: FieldProps<number, TractorArchetype>) => (
                              <Select
                                {...field}
                                value={v.coupler}
                                autoWidth
                                size="small"
                                disabled={disabled}
                                error={Boolean(meta.error)}
                              >
                                <MenuItem
                                  value={
                                    HydraulicCouplerType.HYD_COUPLER_ISO_5675_SIZE_12_5
                                  }
                                >
                                  {couplerTypeToLocaleString(
                                    t,
                                    HydraulicCouplerType.HYD_COUPLER_ISO_5675_SIZE_12_5
                                  )}
                                </MenuItem>
                                <MenuItem
                                  value={
                                    HydraulicCouplerType.HYD_COUPLER_ISO_5675_SIZE_20
                                  }
                                >
                                  {couplerTypeToLocaleString(
                                    t,
                                    HydraulicCouplerType.HYD_COUPLER_ISO_5675_SIZE_20
                                  )}
                                </MenuItem>
                              </Select>
                            )}
                          </Field>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <Button
              className="mx-auto"
              variant="contained"
              startIcon={<AddIcon />}
              disabled={disabled}
              onClick={() =>
                arrayHelpers.push(
                  ScvOutput.fromPartial({
                    id: getOutputGuid(),
                  })
                )
              }
            >
              {t("views.autotractor.equipment.tractors.addScv")}
            </Button>
          </Paper>
        )}
      />
    </div>
  );
};

interface HitchInputProps {
  type: HitchType;
  disabled?: boolean;
}
const HitchInput: FC<HitchInputProps> = ({ type, disabled }) => {
  const { values, setFieldValue } =
    useFormikContext<TractorArchetypeViewModel>();
  const indexOfHitch = (
    hitchType: HitchType,
    values: TractorArchetypeViewModel
  ): number => {
    return values.hitches?.hitches.findIndex((v) => v.type === hitchType) ?? -1;
  };
  const hitchExists = (
    hitchType: HitchType,
    values: TractorArchetypeViewModel
  ): boolean => {
    return indexOfHitch(hitchType, values) >= 0;
  };

  const { t } = useTranslation();
  const { measurementSystem } = useSelf();
  const typeName = `hitches.hitches[${indexOfHitch(type, values)}]`;

  return (
    <div className="flex justify-between min-h-20">
      <FormControlLabel
        checked={hitchExists(type, values)}
        disabled={disabled}
        onChange={() => {
          if (hitchExists(type, values)) {
            setFieldValue("hitches", {
              hitches: values.hitches?.hitches.filter((h) => h.type !== type),
            });
          } else {
            setFieldValue("hitches", {
              hitches: [
                ...(values.hitches?.hitches ?? []),
                { type, offsetYM: 0 },
              ],
            });
          }
        }}
        control={<Checkbox />}
        label={t(hitchTypeToLocaleString(type))}
      />
      {hitchExists(type, values) && (
        <FormikDimensionedNumberInput
          label={
            type === HitchType.HITCH_TYPE_THREE_POINT_FRONT
              ? capitalize(t("models.equipment.distanceAheadFrontAxle"))
              : capitalize(t("models.equipment.distanceBehindFrontAxle"))
          }
          name={`${typeName}.offsetYM`}
          tProps={{ minimum: 0 }}
          fromStored={
            type === HitchType.HITCH_TYPE_THREE_POINT_FRONT
              ? negateVal
              : undefined
          }
          toStored={
            type === HitchType.HITCH_TYPE_THREE_POINT_FRONT
              ? negateVal
              : undefined
          }
          dimensionedNumberInputProps={{
            storedUnit: "m",
            localSystem: measurementSystem,
            disabled,
            displayUnits: { imperial: "in", metric: "cm" },
          }}
        />
      )}
    </div>
  );
};
